import { frameToImageData, type SemanticSegmentationProvider, type SemanticSegmentationResult } from '@limonade/pce-core';

/** Lazy worker keeps inference off the interface thread and never sends images off-device. */
export class LocalPCESemanticProvider implements SemanticSegmentationProvider {
  private worker?: Worker;
  private id = 0;
  private pending = new Map<number, { resolve: (result: SemanticSegmentationResult) => void; reject: (error: Error) => void; timer: ReturnType<typeof setTimeout> }>();

  async segment(frame: VideoFrame | ImageData): Promise<SemanticSegmentationResult> {
    if (!this.worker) {
      this.worker = new Worker(new URL('./pceSemantic.worker.ts', import.meta.url), { type: 'module' });
      this.worker.onmessage = (event: MessageEvent<{ id: number; result?: SemanticSegmentationResult; error?: string }>) => {
        const request = this.pending.get(event.data.id);
        if (!request) return;
        clearTimeout(request.timer);
        this.pending.delete(event.data.id);
        if (event.data.result) request.resolve(event.data.result);
        else request.reject(new Error(event.data.error ?? 'Local detection failed.'));
      };
      this.worker.onerror = () => this.close('Local detection worker could not start; deterministic matching remains available.');
    }
    const image = await frameToImageData(frame);
    const data = new Uint8ClampedArray(image.data);
    const base = new URL('pce-models/', document.baseURI);
    const id = ++this.id;
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => this.close('Local detection timed out; deterministic matching remains available.'), 45_000);
      this.pending.set(id, { resolve, reject, timer });
      this.worker!.postMessage({
        id,
        options: {
          wasmPath: new URL('wasm/', base).href.replace(/\/$/, ''),
          faceModelPath: new URL('blaze_face_full_range.tflite', base).href,
          segmentationModelPath: new URL('selfie_multiclass_256x256.tflite', base).href,
        },
        frame: { width: image.width, height: image.height, data, colorSpace: image.colorSpace },
      }, [data.buffer]);
    });
  }

  close(message = 'Local detection worker closed.'): void {
    this.worker?.terminate();
    this.worker = undefined;
    for (const request of this.pending.values()) {
      clearTimeout(request.timer);
      request.reject(new Error(message));
    }
    this.pending.clear();
  }
}
